import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { DatePipe, NgClass, NgTemplateOutlet } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnDestroy,
    OnInit,
    TemplateRef,
    ViewChild,
    ViewContainerRef,
    ViewEncapsulation,
} from '@angular/core';
import { MatButton, MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router, RouterLink } from '@angular/router';
import { AuthService } from 'app/core/auth/auth.service';
import { NotificationsService } from 'app/layout/common/notifications/notifications.service';
import {
    Notification,
    NotificationEventType,
    NOTIFICATION_CONFIG,
} from 'app/layout/common/notifications/notifications.types';
import { distinctUntilChanged, filter, fromEvent, Subject, takeUntil } from 'rxjs';
import { FcmService } from 'app/core/fcm/fcm.service';
import { ToastrService } from 'ngx-toastr';

@Component({
    selector: 'notifications',
    templateUrl: './notifications.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: 'notifications',
    standalone: true,
    imports: [
        MatButtonModule,
        MatIconModule,
        MatTooltipModule,
        NgClass,
        NgTemplateOutlet,
        RouterLink,
        DatePipe,
    ],
})
export class NotificationsComponent implements OnInit, OnDestroy {
    @ViewChild('notificationsOrigin') private _notificationsOrigin: MatButton;
    @ViewChild('notificationsPanel')
    private _notificationsPanel: TemplateRef<any>;

    notifications: Notification[] = [];
    unreadCount: number = 0;
    notificationConfig = NOTIFICATION_CONFIG;
    private _overlayRef: OverlayRef;
    private _pushActive = false;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _notificationsService: NotificationsService,
        private _overlay: Overlay,
        private _viewContainerRef: ViewContainerRef,
        private _authService: AuthService,
        private _router: Router,
        private _toastr: ToastrService,
        private _fcmService: FcmService
    ) { }

    ngOnInit(): void {
        // Always subscribe first
        this._notificationsService.notifications$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((notifications: Notification[]) => {
                this.notifications = notifications;
                this._calculateUnreadCount();
                this._changeDetectorRef.markForCheck();
            });

        const authUserId = Number(this._authService.getAuthUserIdFromToken());

        if (!Number.isFinite(authUserId) || authUserId <= 0) {
            console.warn('No valid authUserId available for notifications');
            return;
        }

        // Push actif : les nouvelles notifications arrivent d'elles-mêmes, la liste n'est chargée qu'une fois.
        // Sinon (notifications refusées, navigateur incompatible, HTTP), on interroge le serveur chaque minute.
        this._fcmService.pushActive$
            .pipe(distinctUntilChanged(), takeUntil(this._unsubscribeAll))
            .subscribe((pushActive) => {
                this._pushActive = pushActive;
                if (pushActive) {
                    this._notificationsService.stopPolling();
                    this._reload(authUserId);
                } else {
                    this._notificationsService.startPolling(authUserId, 60000);
                }
            });

        // Onglet caché : le push s'affiche en notification système sans passer par la page ; on recharge au retour.
        fromEvent(document, 'visibilitychange')
            .pipe(
                filter(() => this._pushActive && document.visibilityState === 'visible'),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe(() => this._reload(authUserId));
    }

    private _reload(authUserId: number): void {
        this._notificationsService.getAll(authUserId)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe({ error: (error) => console.error('Failed to load notifications:', error) });
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();

        if (this._overlayRef) {
            this._overlayRef.dispose();
        }

        this._notificationsService.stopPolling();
    }

    openPanel(): void {
        if (!this._notificationsPanel || !this._notificationsOrigin) {
            return;
        }

        if (!this._overlayRef) {
            this._createOverlay();
        }

        // ✅ DETACH first if already attached, then attach fresh
        if (this._overlayRef.hasAttached()) {
            this._overlayRef.detach();
        }

        this._overlayRef.attach(
            new TemplatePortal(this._notificationsPanel, this._viewContainerRef)
        );

        const authUserId = Number(this._authService.getAuthUserIdFromToken());
        if (Number.isFinite(authUserId) && authUserId > 0) {
            this._notificationsService.getAll(authUserId)
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe({
                    error: (error) => console.error('Failed to load notifications:', error),
                });
        }
    }

    closePanel(): void {
        if (this._overlayRef && this._overlayRef.hasAttached()) {
            this._overlayRef.detach();
        }
    }

    markAllAsRead(): void {
        this._notificationsService.markAllAsRead().subscribe();
    }

    toggleRead(notification: Notification): void {
        notification.read = !notification.read;
        this._notificationsService.update(notification.id, notification).subscribe();
    }

    deleteNotification(notification: Notification, event: Event): void {
        event.stopPropagation();
        event.preventDefault();
        this._notificationsService.delete(notification.id)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe({
                error: () => this._toastr.error('Impossible de supprimer la notification.'),
            });
    }

    getEventType(notification: Notification): string {
        return notification.eventType ?? notification.data?.['eventType'] ?? 'DEFAULT';
    }

    navigateToNotification(notification: Notification): void {
        if (this.getEventType(notification) !== 'TICKET_OVERDUE') return;
        const data = notification.data || {};
        if (data['sprintId']) {
            this.closePanel();
            void this._router.navigate(['/sprint', data['sprintId']], {
                queryParams: { ticketId: data['ticketId'] || null },
            });
        } else if (data['projectId']) {
            this.closePanel();
            void this._router.navigate(['/projets', data['projectId']]);
        }
    }

    getNotificationConfig(eventType?: string) {
        if (!eventType || !Object.prototype.hasOwnProperty.call(NOTIFICATION_CONFIG, eventType)) {
            return NOTIFICATION_CONFIG['DEFAULT'];
        }
        return NOTIFICATION_CONFIG[eventType as NotificationEventType];
    }

    getNotificationIcon(notification: Notification): string {
        const eventType = this.getEventType(notification);
        return this.getNotificationConfig(eventType).icon;
    }

    getNotificationBodyClasses(notification: Notification): string {
        const eventType = this.getEventType(notification);
        const config = this.getNotificationConfig(eventType);
        return config.backgroundColor;
    }

    getNotificationIconClasses(notification: Notification): string {
        const eventType = this.getEventType(notification);
        const config = this.getNotificationConfig(eventType);
        return config.primaryColor;
    }

    getFormattedMessage(notification: Notification): string {
        const data = notification.data || {};

        switch (this.getEventType(notification)) {
            case 'TICKET_OVERDUE':
                return notification.body;
            case 'TIMESHEET_SUBMITTED':
                return `A resource has submitted a new timesheet.`;
            case 'TIMESHEET_VALIDATED':
                return `Your timesheet has been validated.`;
            case 'CRA_REJECTED':
                return `Your timesheet has been rejected. Please review the comments.`;
            case 'CRA_COMMENT':
                const preview = data['commentPreview']?.substring(0, 50) || 'New comment';
                return `You have a new comment: "${preview}"`;
            default:
                return notification.body || 'New notification';
        }
    }

    getNotificationDetails(notification: Notification): { label: string; value: string }[] {
        const data = notification.data || {};
        const details: { label: string; value: string }[] = [];

        if (this.getEventType(notification) === 'TICKET_OVERDUE' && data['daysOverdue'] != null) {
            details.push({ label: 'Retard', value: `${data['daysOverdue']} j` });
        }

        if (data['companyName']) {
            details.push({ label: 'Company', value: data['companyName'] });
        }
        if (data['timesheetId']) {
            details.push({ label: 'Timesheet ID', value: data['timesheetId'] });
        }
        if (data['authorName']) {
            details.push({ label: 'Author', value: data['authorName'] });
        }
        if (data['status']) {
            details.push({ label: 'Status', value: data['status'] });
        }

        return details;
    }

    trackByFn(index: number, item: any): any {
        return item.id || index;
    }

    private _createOverlay(): void {
        this._overlayRef = this._overlay.create({
            hasBackdrop: true,
            backdropClass: 'fuse-backdrop-on-mobile',
            scrollStrategy: this._overlay.scrollStrategies.block(),
            positionStrategy: this._overlay
                .position()
                .flexibleConnectedTo(this._notificationsOrigin._elementRef.nativeElement)
                .withLockedPosition(true)
                .withPush(true)
                .withPositions([
                    {
                        originX: 'start',
                        originY: 'bottom',
                        overlayX: 'start',
                        overlayY: 'top',
                    },
                    {
                        originX: 'start',
                        originY: 'top',
                        overlayX: 'start',
                        overlayY: 'bottom',
                    },
                    {
                        originX: 'end',
                        originY: 'bottom',
                        overlayX: 'end',
                        overlayY: 'top',
                    },
                    {
                        originX: 'end',
                        originY: 'top',
                        overlayX: 'end',
                        overlayY: 'bottom',
                    },
                ]),
        });

        this._overlayRef.backdropClick().subscribe(() => {
            this._overlayRef.detach();
        });
    }

    private _calculateUnreadCount(): void {
        this.unreadCount = this.notifications?.filter((n) => !n.read).length || 0;
    }

    /**
 * Safe date formatting
 */
    getFormattedDate(date: string | Date | undefined): string {
        if (!date) return '';

        try {
            const d = typeof date === 'string' ? new Date(date) : date;
            if (isNaN(d.getTime())) {
                return 'Invalid date';
            }
            return d.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                hour12: true,
            });
        } catch {
            return 'Invalid date';
        }
    }
}
